import React, { useState, useEffect, useCallback } from 'react';
import {
    Table, DatePicker, Select, Button, Tag, Space, Card,
    Tooltip, Input, message, Divider, Typography,
    Drawer, Descriptions, Empty
} from 'antd';
import { useAuth } from '../../context/AuthContext';
import {
    SearchOutlined, DownloadOutlined, PlayCircleOutlined,
    EyeOutlined, PhoneOutlined,
    ClockCircleOutlined, UserOutlined, DeploymentUnitOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { reportService, pbxReportCatalogService } from '../../services/api';
import RecordingPlayerModal from '../../components/RecordingPlayerModal';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text, Title } = Typography;

const ReportCDR = () => {
    const auth = useAuth() || {};
    const permissions = Array.isArray(auth.permissions) ? auth.permissions : [];
    const canExport = permissions.includes('callcenter.reports.cdr.export');
    const canListen = permissions.includes('recordings.listen');
    const canDownload = permissions.includes('recordings.download');

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 15 });

    // Drawer state
    const [detailVisible, setDetailVisible] = useState(false);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [selectedCall, setSelectedCall] = useState(null);

    // Recording state
    const [recordingModalVisible, setRecordingModalVisible] = useState(false);
    const [currentRecordingUrl, setCurrentRecordingUrl] = useState(null);
    const [currentRecordingId, setCurrentRecordingId] = useState(null);

    // Filters state
    const [filters, setFilters] = useState({
        date_range: [dayjs().startOf('day'), dayjs().endOf('day')],
        phone: '',
        direction: undefined,
        status: undefined,
        user_id: undefined,
        campaign_id: undefined,
    });

    // Catalogs
    const [campaigns, setCampaigns] = useState([]);
    const [users, setUsers] = useState([]);

    const fetchCatalogs = async () => {
        try {
            const [campRes, userRes] = await Promise.all([
                pbxReportCatalogService.getCampaigns(),
                pbxReportCatalogService.getUsersWithExtension()
            ]);
            if (campRes.status === 'success') setCampaigns(campRes.data);
            if (userRes.status === 'success') setUsers(userRes.data);
        } catch (error) {
            console.error("Error fetching catalogs", error);
        }
    };

    const fetchData = useCallback(async (page = 1, pageSize = 15) => {
        setLoading(true);
        try {
            const params = {
                page,
                limit: pageSize,
                ...filters,
                date_range: filters.date_range ? filters.date_range.map(d => d.format('YYYY-MM-DD')).join(',') : null
            };

            const response = await reportService.getCDR(params);
            if (response.status === 'success') {
                setData(response.data.items);
                setTotal(response.data.total);
                setPagination({ current: page, pageSize });
            }
        } catch (error) {
            message.error("Error al cargar los datos del reporte");
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchCatalogs();
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleTableChange = (pag) => {
        fetchData(pag.current, pag.pageSize);
    };

    const handleExport = () => {
        const params = {
            ...filters,
            date_range: filters.date_range ? filters.date_range.map(d => d.format('YYYY-MM-DD')).join(',') : null
        };
        const url = reportService.getCDRExportUrl(params);
        window.open(url, '_blank');
    };

    const showDetail = async (id) => {
        setDetailVisible(true);
        setLoadingDetail(true);
        try {
            const response = await reportService.getCallDetail(id);
            if (response.status === 'success') {
                setSelectedCall(response.data);
            }
        } catch (error) {
            message.error("Error al cargar el detalle de la llamada");
        } finally {
            setLoadingDetail(false);
        }
    };

    const handlePlay = (id) => {
        const url = reportService.getRecordingUrl(id);
        setCurrentRecordingId(id);
        setCurrentRecordingUrl(url);
        setRecordingModalVisible(true);
    };

    const formatDuration = (s) => {
        if (s === null || s === undefined) return '0:00';
        const min = Math.floor(s / 60);
        const sec = s % 60;
        return `${min}:${sec.toString().padStart(2, '0')}`;
    };

    const getStatusTag = (status) => {
        let color = 'default';
        let label = status;
        if (status === 'answered') { color = 'success'; label = 'Contestada'; }
        if (status === 'no_answer') { color = 'warning'; label = 'No Contesta'; }
        if (status === 'busy') { color = 'error'; label = 'Ocupado'; }
        if (status === 'failed') { color = 'error'; label = 'Fallida'; }
        if (status === 'abandoned') { color = 'magenta'; label = 'Abandonada'; }
        if (status === 'rejected') { color = 'error'; label = 'Rechazada'; }
        if (status === 'in_queue') { color = 'processing'; label = 'En Cola'; }
        if (status === 'in_call') { color = 'processing'; label = 'En Llamada'; }
        return <Tag color={color}>{label.toUpperCase()}</Tag>;
    };

    const renderDynamicForm = () => {
        if (!selectedCall || !selectedCall.form_fields || selectedCall.form_fields.length === 0) {
            return <Empty description="Sin datos de formulario" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
        }

        let formData = {};
        try {
            formData = typeof selectedCall.call.form_data === 'string'
                ? JSON.parse(selectedCall.call.form_data)
                : (selectedCall.call.form_data || {});
        } catch (e) {
            console.error("Error parsing form_data", e);
        }

        return (
            <Descriptions bordered column={1} size="small">
                {selectedCall.form_fields.map(field => (
                    <Descriptions.Item key={field.field_key} label={field.label}>
                        {formData[field.field_key] || <Text type="secondary">-</Text>}
                    </Descriptions.Item>
                ))}
            </Descriptions>
        );
    };

    const columns = [
        {
            title: 'Fecha y Hora',
            dataIndex: 'call_start',
            key: 'call_start',
            width: 170,
            render: (text) => dayjs(text).format('DD/MM/YYYY HH:mm:ss'),
        },
        {
            title: 'Dirección',
            dataIndex: 'direction',
            key: 'direction',
            width: 100,
            render: (dir) => (
                <Tag color={dir === 'inbound' ? 'blue' : 'purple'}>
                    {dir === 'inbound' ? 'Entrante' : 'Saliente'}
                </Tag>
            ),
        },
        {
            title: 'Campaña',
            dataIndex: 'campaign_name',
            key: 'campaign_name',
            width: 150,
            render: (val) => val || <Tag>N/A</Tag>
        },
        {
            title: 'Origen',
            dataIndex: 'source',
            key: 'source',
        },
        {
            title: 'Destino',
            dataIndex: 'destination',
            key: 'destination',
        },
        {
            title: 'Estado',
            dataIndex: 'status',
            key: 'status',
            render: (status) => getStatusTag(status),
        },
        {
            title: 'Duración',
            dataIndex: 'talk_seconds',
            key: 'talk_seconds',
            render: (s, record) => record.status === 'in_call' ? '--:--' : formatDuration(s)
        },
        {
            title: 'En Espera',
            dataIndex: 'wait_seconds',
            key: 'wait_seconds',
            render: (s) => formatDuration(s)
        },
        {
            title: 'Usuario',
            dataIndex: 'agent_name',
            key: 'agent_name',
            render: (val) => val || <Text type="secondary">N/A</Text>
        },
        {
            title: 'Acciones',
            key: 'actions',
            width: 120,
            fixed: 'right',
            render: (_, record) => (
                <Space size="middle">
                    <Tooltip title="Ver detalle">
                        <Button
                            shape="circle"
                            icon={<EyeOutlined />}
                            onClick={() => showDetail(record.id)}
                        />
                    </Tooltip>
                    {record.recording_path && canListen && (
                        <Tooltip title="Reproducir">
                            <Button
                                shape="circle"
                                icon={<PlayCircleOutlined />}
                                style={{ color: '#1890ff' }}
                                onClick={() => handlePlay(record.id)}
                            />
                        </Tooltip>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div className="report-cdr fade-in">
            {/* Breadcrumb removed per request */}

            <Card bordered={false} className="report-card" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                        <RangePicker
                            value={filters.date_range}
                            onChange={(val) => setFilters({ ...filters, date_range: val })}
                            placeholder={['Inicio', 'Fin']}
                            style={{ width: 280 }}
                        />
                        <Input
                            placeholder="Número (Origen/Destino)"
                            value={filters.phone}
                            onChange={(e) => setFilters({ ...filters, phone: e.target.value })}
                            style={{ width: 200 }}
                            prefix={<SearchOutlined />}
                            allowClear
                        />
                        <Select
                            placeholder="Dirección"
                            value={filters.direction}
                            onChange={(val) => setFilters({ ...filters, direction: val })}
                            style={{ width: 130 }}
                            allowClear
                        >
                            <Option value="inbound">Entrante</Option>
                            <Option value="outbound">Saliente</Option>
                        </Select>
                        <Select
                            placeholder="Estado"
                            value={filters.status}
                            onChange={(val) => setFilters({ ...filters, status: val })}
                            style={{ width: 130 }}
                            allowClear
                        >
                            <Option value="answered">Contestada</Option>
                            <Option value="no_answer">No Contesta</Option>
                            <Option value="busy">Ocupado</Option>
                            <Option value="failed">Fallida</Option>
                            <Option value="abandoned">Abandonada</Option>
                            <Option value="rejected">Rechazada</Option>
                            <Option value="in_queue">En Cola</Option>
                            <Option value="in_call">En Llamada</Option>
                        </Select>
                        <Select
                            placeholder="Campaña"
                            value={filters.campaign_id}
                            onChange={(val) => setFilters({ ...filters, campaign_id: val })}
                            style={{ width: 180 }}
                            allowClear
                            showSearch
                            optionFilterProp="children"
                        >
                            {campaigns.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
                        </Select>
                        <Select
                            placeholder="Usuario"
                            value={filters.user_id}
                            onChange={(val) => setFilters({ ...filters, user_id: val })}
                            style={{ width: 180 }}
                            allowClear
                            showSearch
                            optionFilterProp="children"
                        >
                            {users.map(u => <Option key={u.id} value={u.id}>{u.name}</Option>)}
                        </Select>

                        <Button type="primary" icon={<SearchOutlined />} onClick={() => fetchData(1, pagination.pageSize)} loading={loading}>
                            Filtrar
                        </Button>
                        {canExport && (
                            <Button icon={<DownloadOutlined />} onClick={handleExport} disabled={data.length === 0}>
                                Exportar
                            </Button>
                        )}
                    </div>

                    <Table
                        columns={columns}
                        dataSource={data}
                        loading={loading}
                        rowKey="id"
                        pagination={{
                            ...pagination,
                            total,
                            showSizeChanger: true,
                            pageSizeOptions: ['15', '30', '50', '100'],
                            showTotal: (total) => `Total: ${total} registros`
                        }}
                        onChange={handleTableChange}
                        size="middle"
                        scroll={{ x: 1000 }}
                    />
                </Space>
            </Card>

            <Drawer
                title={
                    <Space>
                        <EyeOutlined />
                        Detalle de Llamada
                    </Space>
                }
                placement="right"
                width={500}
                onClose={() => setDetailVisible(false)}
                open={detailVisible}
                loading={loadingDetail}
            >
                {selectedCall && (
                    <div className="fade-in">
                        <Title level={5}><PhoneOutlined /> Información Técnica</Title>
                        <Descriptions column={1} size="small" bordered>
                            <Descriptions.Item label="Fecha Inicio">{dayjs(selectedCall.call.call_start).format('DD/MM/YYYY HH:mm:ss')}</Descriptions.Item>
                            <Descriptions.Item label="Estado">{getStatusTag(selectedCall.call.status)}</Descriptions.Item>
                            <Descriptions.Item label="Origen">{selectedCall.call.source}</Descriptions.Item>
                            <Descriptions.Item label="Destino">{selectedCall.call.destination}</Descriptions.Item>
                            <Descriptions.Item label="DID">{selectedCall.call.did || '-'}</Descriptions.Item>
                        </Descriptions>

                        <Divider />

                        <Title level={5}><ClockCircleOutlined /> Métricas de Tiempo</Title>
                        <Descriptions column={2} size="small" bordered>
                            <Descriptions.Item label="Total">{formatDuration(selectedCall.call.duration_seconds)}</Descriptions.Item>
                            <Descriptions.Item label="Espera">{formatDuration(selectedCall.call.wait_seconds)}</Descriptions.Item>
                            <Descriptions.Item label="Habla">{formatDuration(selectedCall.call.talk_seconds)}</Descriptions.Item>
                        </Descriptions>

                        <Divider />

                        {selectedCall.call.participant_id && (
                            <>
                                <Title level={5}><UserOutlined /> Información del Participante</Title>
                                <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
                                    <Descriptions.Item label="Código Hogar">{selectedCall.call.participant_homecode || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="Nombre">{selectedCall.call.participant_name || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="Documento">{selectedCall.call.document_type} - {selectedCall.call.document_number}</Descriptions.Item>
                                </Descriptions>
                                <Divider />
                            </>
                        )}

                        <Title level={5}><UserOutlined /> Asignación</Title>
                        <Descriptions column={1} size="small" bordered>
                            <Descriptions.Item label="Usuario">{selectedCall.call.agent_name || 'No asignado'}</Descriptions.Item>
                            <Descriptions.Item label="Campaña">{selectedCall.call.campaign_name || 'Llamada directa'}</Descriptions.Item>
                        </Descriptions>

                        <Divider />

                        <Title level={5}><DeploymentUnitOutlined /> Datos del Formulario</Title>
                        {renderDynamicForm()}
                    </div>
                )}
            </Drawer>

            <RecordingPlayerModal
                open={recordingModalVisible}
                onClose={() => {
                    setRecordingModalVisible(false);
                    setCurrentRecordingUrl(null);
                    setCurrentRecordingId(null);
                }}
                recordingUrl={currentRecordingUrl}
                downloadUrl={canDownload && currentRecordingId ? reportService.getRecordingUrl(currentRecordingId, true) : null}
                title="Reproductor de Audio"
            />
        </div>
    );
};

export default ReportCDR;
